<template>
  <div :class="t.wrapper">
    <label :class="t.label">
      {{ question.prompt }}
      <span v-if="!question.required" class="text-gray-400 font-normal">(optional)</span>
    </label>
    <p
      v-if="question.subtext"
      :class="t.subtext"
      v-html="question.subtext"
    ></p>
    <input
      v-model="displayValue"
      type="text"
      inputmode="numeric"
      :class="[t.input, error ? 'border-red-500' : 'border-gray-300']"
      :style="inputFocusStyle"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleInput"
      placeholder="MM/DD/YYYY"
    />
    <p v-if="error" :class="t.error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { tokensFor } from './size_tokens';

export default {
  name: 'QuestionDate',
  props: {
    question: {
      type: Object,
      required: true,
    },
    existingAnswer: {
      type: Object,
      default: null,
    },
    size: {
      type: String,
      default: 'kiosk',
      validator: (v) => ['kiosk', 'form'].includes(v),
    },
  },
  data() {
    return {
      // Stored format: mm/dd/yyyy
      answer: '',
      displayValue: '',
      originalAnswer: '',
      error: false,
      errorMessage: 'Please enter a valid date.',
      isFocused: false,
    };
  },
  computed: {
    t() {
      return tokensFor(this.size);
    },
    inputFocusStyle() {
      if (this.isFocused) {
        return {
          borderColor: 'var(--team-primary)',
          boxShadow: '0 0 0 3px rgba(var(--team-primary-rgb, 55, 65, 81), 0.1)',
        };
      }
      return {};
    },
    dirty() {
      return this.answer !== this.originalAnswer;
    },
    valid() {
      if (this.question.required) {
        return this.isValidDate(this.answer);
      }
      // If not required, empty is valid
      if (this.answer.length === 0) return true;
      return this.isValidDate(this.answer);
    },
  },
  watch: {
    answer() {
      this.emit();
      if (this.valid) {
        this.error = false;
      }
    },
  },
  created() {
    if (this.existingAnswer && this.existingAnswer.answer) {
      this.answer = this.existingAnswer.answer;
      this.originalAnswer = this.existingAnswer.answer;
      this.displayValue = this.answer;
    }
    this.$notificationManager?.on('show-reg-errors', this.validate);
  },
  destroyed() {
    this.$notificationManager?.off('show-reg-errors', this.validate);
  },
  methods: {
    handleInput(event) {
      // Extract only digits
      const digits = event.target.value.replace(/\D/g, '');
      // Auto-format as MM/DD/YYYY
      this.displayValue = this.formatDate(digits);
      this.answer = this.displayValue;
    },
    formatDate(digits) {
      if (!digits) return '';
      if (digits.length <= 2) {
        return digits;
      }
      if (digits.length <= 4) {
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    },
    isValidDate(dateStr) {
      if (!dateStr || dateStr.length === 0) return false;
      // Check format MM/DD/YYYY
      const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const match = dateStr.match(regex);
      if (!match) return false;

      const month = parseInt(match[1], 10);
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      // Basic validation
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      if (year < 1900 || year > 2100) return false;

      // Check actual date validity
      const date = new Date(year, month - 1, day);
      return (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      );
    },
    emit() {
      this.$emit('answer-changed', this.question, this.answer, this.dirty, this.valid);
    },
    validate() {
      if (!this.valid) {
        this.error = true;
        if (this.question.required && this.answer.length === 0) {
          this.errorMessage = 'Please enter a date.';
        } else {
          this.errorMessage = 'Please enter a valid date (MM/DD/YYYY).';
        }
      } else {
        this.error = false;
      }
    },
    handleFocus() {
      this.isFocused = true;
    },
    handleBlur() {
      this.isFocused = false;
      this.validate();
    },
  },
};
</script>
